import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Play, Edit, Copy, MoreVertical, Power, PowerOff, FolderInput, Trash2, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TableRow, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { memo } from 'react';

interface Workflow {
  id: string;
  name: string;
  description?: string;
  active: boolean;
  createdAt?: string;  // ISO string from backend
  updatedAt?: string;  // ISO string from backend
  folderId?: string | null;
}

interface DraggableWorkflowRowProps {
  workflow: Workflow;
  onNavigate: () => void;
  onRun: (workflow: Workflow) => void;
  onToggleActive: (workflow: Workflow) => void;
  onDuplicate: (workflow: Workflow) => void;
  onMove: (workflow: Workflow) => void;
  onMoveToTeam?: (workflow: Workflow) => void;
  onDelete: (id: string, name: string) => void;
  isRunning: boolean;
  formatTimestamp: (timestamp: string | number) => string;
}

export const DraggableWorkflowRow = memo(function DraggableWorkflowRow({ 
  workflow,
  onNavigate,
  onRun,
  onToggleActive,
  onDuplicate,
  onMove,
  onMoveToTeam,
  onDelete,
  isRunning,
  formatTimestamp,
}: DraggableWorkflowRowProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: workflow.id,
    data: {
      type: 'workflow',
      workflow: workflow,
    },
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      className={cn(
        'cursor-pointer hover:bg-muted/50 transition-colors',
        isDragging && 'z-50 shadow-lg ring-2 ring-primary',
        !workflow.active && 'opacity-60'
      )}
      onClick={onNavigate}
    >
      <TableCell>
        <div className="flex items-center gap-3">
          <div
            {...listeners}
            {...attributes}
            className="cursor-grab active:cursor-grabbing"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={cn(
              "h-5 w-2 transition-colors rounded",
              workflow.active 
                ? "bg-primary hover:bg-primary/80" 
                : "bg-muted-foreground/20 hover:bg-muted-foreground/40"
            )} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">{workflow.name}</div>
            {workflow.description && (
              <div className="text-sm text-muted-foreground truncate">
                {workflow.description}
              </div>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell>
        <Badge 
          variant={workflow.active ? 'default' : 'secondary'}
          className="cursor-pointer hover:opacity-80 transition-opacity"
          onClick={(e) => {
            e.stopPropagation();
            onToggleActive(workflow);
          }}
        >
          {workflow.active ? 'Active' : 'Inactive'}
        </Badge>
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {workflow.createdAt ? formatTimestamp(workflow.createdAt) : '-'}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {workflow.updatedAt ? formatTimestamp(workflow.updatedAt) : '-'}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onRun(workflow);
            }}
            disabled={isRunning || !workflow.active}
          >
            <Play className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate();
            }}
          >
            <Edit className="h-4 w-4" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="sm">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(e) => {
                e.stopPropagation();
                onDuplicate(workflow);
              }}>
                <Copy className="h-4 w-4 mr-2" />
                Duplicate
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => {
                e.stopPropagation();
                onMove(workflow);
              }}>
                <FolderInput className="h-4 w-4 mr-2" />
                Move to Personal Folder
              </DropdownMenuItem>
              {onMoveToTeam && (
                <DropdownMenuItem onClick={(e) => {
                  e.stopPropagation();
                  onMoveToTeam(workflow);
                }}>
                  <Users className="h-4 w-4 mr-2" />
                  Move to Team Folder
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={(e) => {
                e.stopPropagation();
                onToggleActive(workflow);
              }}>
                {workflow.active ? (
                  <>
                    <PowerOff className="h-4 w-4 mr-2" />
                    Deactivate
                  </>
                ) : (
                  <>
                    <Power className="h-4 w-4 mr-2" />
                    Activate
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(workflow.id, workflow.name);
                }}
                className="text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TableCell>
    </TableRow>
  );
});
